import React, { useState, useEffect, useCallback } from 'react';
import { memoNamed } from '../utils/react/memo-named';
import firebase from 'firebase/app';
import { StringInput } from './common/input';
import { Page } from './App';
import { Explanation, Message, H3, Hr } from './common/styled';
import styled from 'styled-components';

const MessageBlue = styled.div`
  display: inline-block;
  font-size: 11px;
  color: deepskyblue;
`;

interface Props {
  gotoPage: (page: Page) => void;
}

export const Setting = memoNamed<Props>('Setting', (props) => {
  const [email, setEmail] = useState<string>('');
  const [password1, setPassword1] = useState<string>('');
  const [password2, setPassword2] = useState<string>('');
  const [passwordEdited, setPasswordEdited] = useState<boolean>(false);
  const [passwordIsCorrect, setPasswordIsCorrect] = useState<boolean>(true);
  const [passwordAlert, setPasswordAlert] = useState<string>('');
  const [passwordIsUpdating, setPasswordIsUpdating] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteMessage, setDeleteMessage] = useState<string>('');
  const [doneDelete1, setDoneDelete1] = useState<boolean>(false);
  const [doneDelete2, setDoneDelete2] = useState<boolean>(false);
  const [EmailUpdating, setEmailUpdating] = useState<boolean>(false);
  const [EmailMessage, setEmailMessage] = useState<string>('');
  const [emailEdited, setEmailEdited] = useState<boolean>(false);

  //Check password
  useEffect(() => {
    setPasswordEdited(false);
    if (password1 !== password2) {
      setPasswordIsCorrect(false);
      setPasswordAlert('パスワードが一致しません。');
    } else {
      setPasswordIsCorrect(true);
      setPasswordAlert('');
    }
  }, [password1, password2]);

  //Get Email Address
  useEffect(() => {
    const user = firebase.auth().currentUser;
    setEmail(user?.email ?? '');
  }, []);

  const updatePassword = useCallback(() => {
    const user = firebase.auth().currentUser;
    if (passwordIsCorrect) {
      setPasswordIsUpdating(true);
      user
        ?.updatePassword(password1)
        .then(() => {
          setPasswordIsUpdating(false);
          setPasswordEdited(true);
        })
        .catch((error) => {
          console.error(error);
          setPasswordIsUpdating(false);
          if (error.code === 'auth/requires-recent-login') {
            setPasswordAlert(
              'パスワードの更新に失敗しました。この操作には本人確認が必要です。ログアウトをクリックし、再度ログインしなおしてからお試しください。'
            );
            return;
          }
          setPasswordAlert('パスワードの更新に失敗しました。');
        });
    }
  }, [passwordIsCorrect, password1]);

  const toggleConfirm = useCallback(() => {
    setConfirmDelete((confirmDelete) => !confirmDelete);
  }, []);

  const deleteAccount = useCallback(() => {
    const user = firebase.auth().currentUser;
    const userID = user?.uid;
    setDeleting(true);
    //firestoreからの削除
    const db = firebase.firestore();
    db.collection('Users')
      .where('userID', '==', userID)
      .get()
      .then((snapshot) => {
        if (snapshot.size === 0) {
          setDeleteMessage('ユーザIDに一致するデータがありません。');
        } else if (snapshot.size === 1) {
          const docID = snapshot.docs[0].id;
          db.collection('Users')
            .doc(docID)
            .delete()
            .then(() => {
              setDoneDelete1(true);
            });
        } else {
          setDeleteMessage(
            'ユーザIDに一致するデータが複数あります。管理者に問い合わせて下さい。'
          );
        }
      })
      .catch((error) => {
        console.error(error);
        setDeleteMessage('docIDデータの取得に失敗しました。');
      });
    //アカウントの削除
    user
      ?.delete()
      .then(() => {
        setDoneDelete2(true);
      })
      .catch((error) => {
        console.error(error);
        setDeleteMessage(
          'アカウントの消去に失敗しました。管理者(togen.meibo@gmail.com)にログイン用メールアドレスと名前をお伝えください。'
        );
      });
  }, []);

  //削除ができたら、Loginページに移行
  useEffect(() => {
    if (doneDelete1 && doneDelete2) {
      props.gotoPage('LoginPage');
    }
  }, [props, doneDelete1, doneDelete2]);

  // Update Email
  const updateEmail = useCallback(() => {
    const user = firebase.auth().currentUser;
    setEmailUpdating(true);
    setEmailMessage('');
    user
      ?.updateEmail(email)
      .then(() => {
        setEmailUpdating(false);
        setEmailEdited(true);
      })
      .catch((error) => {
        setEmailUpdating(false);
        setEmail(user?.email ?? '');
        console.error(error);
        if (error.code === 'auth/requires-recent-login') {
          setEmailMessage(
            '変更に失敗しました。この操作には本人確認が必要です。再度ログインしなおしてからお試しください。'
          );
          return;
        }
        setEmailMessage('メールアドレスの変更に失敗しました。');
      });
  }, [email]);

  // onChangeEmail
  useEffect(() => {
    setEmailEdited(false);
  }, [email]);

  // style
  const emailStyle = emailEdited ? { color: 'deepskyblue' } : {};
  const passwordStyle = passwordEdited ? { color: 'deepskyblue' } : {};

  return (
    <div>
      <H3>ログイン用メールアドレスの変更</H3>
      <Hr />
      <Explanation>
        ログイン用メールアドレスを以下の欄に入力し、変更ボタンをクリックしてください。桃源会からの連絡用のメールアドレスを変更したい場合は、この欄ではなく、上部の連絡先の欄から設定してください。
      </Explanation>
      <StringInput
        value={email}
        onValueChange={setEmail}
        style={emailStyle}
        autoComplete='off'
      />
      <MessageBlue>{emailEdited ? '【保存済】' : undefined}</MessageBlue>
      <br />
      <button onClick={updateEmail}>変更</button>
      {EmailUpdating ? ' 通信中...' : undefined}
      <Message>{EmailMessage}</Message>
      <br />
      <H3>ログイン用パスワードの変更</H3>
      <Hr />
      新しいパスワード
      <StringInput
        value={password1}
        onValueChange={setPassword1}
        style={passwordStyle}
        type='password'
        autoComplete='off'
      />
      <br />
      もう一度入力
      <StringInput
        value={password2}
        onValueChange={setPassword2}
        style={passwordStyle}
        type='password'
        autoComplete='off'
      />
      <Message>{passwordAlert}</Message>
      <button onClick={updatePassword}>パスワードを変更</button>
      <MessageBlue>{passwordEdited ? '【保存済】' : undefined}</MessageBlue>
      {passwordIsUpdating ? ' 通信中...' : undefined}
      <br />
      <br />
      <H3>アカウントの削除</H3>
      <Hr />
      <Explanation>
        桃源会から退会し、アカウントを削除します。名簿から全データが消去され、システム上にも残りません。この操作は取り消せません。
        <br />
        ※【この操作は再度ログインしてからその直後に行ってください。】
      </Explanation>
      {confirmDelete ? (
        <div>
          本当にアカウントを削除しますか？
          <button onClick={deleteAccount}>削除</button>
          {deleting ? (
            ' 通信中...'
          ) : (
            <button onClick={toggleConfirm}>いいえ</button>
          )}
          <Message>{deleteMessage}</Message>
        </div>
      ) : (
        <div>
          <button onClick={toggleConfirm}>削除する</button>
        </div>
      )}
    </div>
  );
});
